<template>
  <div class="command-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:command-line" width="24" height="24" />
        创作指令管理
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.instruct_name" placeholder="请输入指令名称" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增指令</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="instructionList" stripe v-loading="loading" class="command-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column prop="instruct_name" label="指令名称" width="180" />
          <el-table-column prop="type_name" label="指令类型" width="120">
            <template #default="{ row }">
              <span :class="['type-tag', `type-${row.instruct_type}`]">
                {{ row.type_name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="内容预览" min-width="200">
            <template #default="{ row }">
              <div class="content-preview">
                {{ row.content_preview }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="notes" label="备注" width="150" />
          <el-table-column prop="create_time" label="创建时间" width="180" />
          <el-table-column prop="update_time" label="更新时间" width="180" />

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchInstructions" @current-change="fetchInstructions" background />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <el-form ref="instructionForm" :model="instructionForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="指令名称" prop="instruct_name">
          <el-input v-model="instructionForm.instruct_name" placeholder="请输入指令名称" size="large" />
        </el-form-item>

        <el-form-item label="指令类型" prop="instruct_type">
          <el-select v-model="instructionForm.instruct_type" placeholder="请选择类型" size="large">
            <el-option :value="1" label="描述创作" />
            <el-option :value="2" label="标题创作" />
            <el-option :value="3" label="话题创作" />
            <el-option :value="4" label="文案创作" />
          </el-select>
        </el-form-item>

        <el-form-item label="指令内容" prop="instruct_content">
          <el-input v-model="instructionForm.instruct_content" type="textarea" :rows="6" placeholder="请输入详细的指令内容" />
          <div class="template-buttons">
            <el-button type="primary" size="small" @click="fillTemplate(1)"
              :class="{ active: instructionForm.instruct_type === 1 }">填充描述模板</el-button>
            <el-button type="success" size="small" @click="fillTemplate(2)"
              :class="{ active: instructionForm.instruct_type === 2 }">填充标题模板</el-button>
            <el-button type="warning" size="small" @click="fillTemplate(3)"
              :class="{ active: instructionForm.instruct_type === 3 }">填充话题模板</el-button>
            <el-button type="danger" size="small" @click="fillTemplate(4)"
              :class="{ active: instructionForm.instruct_type === 4 }">填充文案模板</el-button>
          </div>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="instructionForm.notes" placeholder="请输入备注信息" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitInstructionForm">确认</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'SystemCommand',
  components: { Icon },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增指令',
      isEditMode: false,
      searchForm: { instruct_name: '' },
      instructionForm: { id: null, instruct_name: '', instruct_type: 1, instruct_content: '', notes: '' },
      formRules: {
        instruct_name: [{ required: true, message: '请输入指令名称', trigger: 'blur' }],
        instruct_type: [{ required: true, message: '请选择指令类型', trigger: 'change' }],
        instruct_content: [{ required: true, message: '请输入指令内容', trigger: 'blur' }]
      },
      instructionList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '620px'
    },
    dialogTop() {
      if (this.screenWidth < 576) return '5vh'
      return '10vh'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '120px'
    }
  },
  mounted() {
    this.fetchInstructions();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.screenWidth = window.innerWidth },
    async fillTemplate(type) {
      try {
        if (this.instructionForm.instruct_type !== type) { this.instructionForm.instruct_type = type; }
        let templateName = '';
        switch (type) { case 1: templateName = 'desc'; break; case 2: templateName = 'title'; break; case 3: templateName = 'topic'; break; case 4: templateName = 'wenan'; break; }
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/CreationInstructionsController.php`, { params: { template: templateName, t: Date.now() }, responseType: 'text' });
        this.instructionForm.instruct_content = data;
        ElMessage.success('模板加载成功！');
      } catch (error) {
        ElMessage.error('模板加载失败: ' + (error.message || '请检查网络连接'));
        const defaultTemplates = {
          1: '生成视频描述，围绕关键词，补充信息：[结果/过程/福利]，引导互动：[提问/行动指令]，emoji占比30%，生成JSON格式，键名用"1"到"N"表示，例如："video_descriptions": {"1": "第一条描述","2": "第二条描述","3": "第三条描述"}',
          2: '生成视频标题，围绕关键词，要求：[情绪/痛点]+[悬念/反转]+[短句式]，语言风格[年轻化/网络梗/方言等，生成JSON格式，键名用"1"到"N"表示，例如："video_titles": {"1": "第一条标题","2": "第二条标题","3": "第三条标题"}',
          3: '生成话题，围绕关键词，搭配近期热门话题[挑战/音乐/节日]，要求：1个主话题+2个细分话题，生成的话题请勿携带 # 符号，生成JSON格式，键名用"1"到"N"表示，例如："topics": {"1": "第一条话题","2": "第二条话题","3": "第三条话题"}',
          4: '根据上面这个提示词，生成以下内容，并以JSON格式返回（键名使用英文）- 标题（title，8字以内）- 副标题（subtitle，6字以内）- 底部内容（bottomContent，8字以内）- 字幕（subtitles，200字以内）'
        };
        this.instructionForm.instruct_content = defaultTemplates[type] || '请在此输入指令内容';
      }
    },
    async fetchInstructions() {
      this.loading = true;
      try {
        const params = { action: this.searchForm.instruct_name ? 'search' : 'list', store_id: this.userInfo.id, page: this.pagination.page, page_size: this.pagination.pageSize };
        if (this.searchForm.instruct_name) params.instruct_name = this.searchForm.instruct_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/CreationInstructionsController.php`, params);
        if (data.data) { this.instructionList = data.data; this.pagination.total = data.pagination?.total || data.data.length; }
      } catch (error) { ElMessage.error('获取指令列表失败'); }
      finally { this.loading = false; }
    },
    handleSearch() { this.pagination.page = 1; this.fetchInstructions(); },
    openAddDialog() {
      this.dialogTitle = '新增指令'; this.isEditMode = false;
      this.instructionForm = { id: null, instruct_name: '', instruct_type: 1, instruct_content: '', notes: '' };
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.dialogTitle = '编辑指令'; this.isEditMode = true;
      this.instructionForm = { id: row.id, instruct_name: row.instruct_name, instruct_type: row.instruct_type, instruct_content: row.instruct_content, notes: row.notes };
      this.dialogVisible = true;
    },
    submitInstructionForm() {
      this.$refs.instructionForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = { ...this.instructionForm, store_id: this.userInfo.id };
            const { data } = await axios.post(`${config.BASE_URL}/shop/api/CreationInstructionsController.php`, { action, ...payload });
            if (data.success) { ElMessage.success(this.isEditMode ? '更新成功' : '添加成功'); this.dialogVisible = false; this.fetchInstructions(); }
          } catch (error) { ElMessage.error('操作失败: ' + (error.response?.data?.error || error.message)); }
        }
      });
    },
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该指令吗？', '警告', { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/CreationInstructionsController.php`, { action: 'delete', id: id, store_id: this.userInfo.id });
        if (data.success) { ElMessage.success('删除成功'); this.fetchInstructions(); }
      } catch (error) { if (error !== 'cancel') ElMessage.error('删除失败: ' + (error.response?.data?.error || error.message)); }
    }
  }
}
</script>

<style scoped lang="scss">
.command-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .search-left .search-btn {
      width: 100%;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;
  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.type-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.type-1 {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
  }

  &.type-2 {
    background: rgba(34, 197, 94, 0.2);
    color: #4ade80;
  }

  &.type-3 {
    background: rgba(245, 158, 11, 0.2);
    color: #fbbf24;
  }

  &.type-4 {
    background: rgba(234, 98, 14, 0.2);
    color: #fb923c;
  }
}

.content-preview {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  max-height: 3em;
  word-break: break-all;
  color: #a0a0c0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;

  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }

  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.template-buttons {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  .el-button {
    flex: 1;
    min-width: 120px;
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.custom-dialog) .el-dialog__body {
  background: rgba(18, 14, 33, 0.98) !important;
}
</style>